/**
 * Cerimônia do signatário — DOCUMENTO-CÊNTRICA desde a D-063 (#91): o documento
 * (PdfViewer, bytes reais via /content) ocupa a área principal; o FLUXO
 * (identificação → canal/OTP na Simples → consentimento → assinatura) vive na
 * sidebar à esquerda, junto com a trilha de participantes. Estados terminais e
 * de carga continuam vindo da máquina pura `resolveCeremonyScreen` (ordem
 * sensível — não reordenar sem os testes).
 *
 * Retomada (#96): o passo inicial deriva de `channel_verified_at`/
 * `consent_given_at` (`lib/ceremonySteps.ts`) — OTP verificado não se refaz e
 * consentimento registrado nunca "some" da UI.
 *
 * Filtro de certificado por CPF (#92): `certificate-expectation` + espelho do
 * hash em `lib/cpf.ts`. Certificado incompatível NUNCA renderiza. A autoridade
 * continua sendo `checkCertificateBinding` no servidor (D-035).
 *
 * Consentimento (#93): passo comum às duas modalidades (ConsentCard), ANTES do
 * certificado na Qualificada, sempre com o documento em tela — "Li e concordo"
 * só clicável com o viewer carregado.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EnvelopeView, VisualPlacement } from '@argink/shared';
import { BrandMark, Button, Card, TextField, TrailStepper } from '@argink/ui';
import {
  ApiError,
  completeSession,
  getCertificateExpectation,
  getEnvelope,
  giveConsent,
  openSession,
  setVisualPlacement,
  requestOtp,
  signedDownloadPath,
  verifyOtp,
  type CompleteSessionBody,
} from '../lib/api';
import { type AgentCert } from '../lib/agent';
import { providerCertChain, signWithProvider } from '../lib/signing-provider';
import { useBridgeOnboarding } from '../lib/useBridgeOnboarding';
import { getBridgeDownloadConfig } from '../lib/bridge-download';
import { describeError, specificSimpleError, type HumanError } from '../lib/errors';
import { newTraceId } from '../lib/trace';
import { formatDate, maskEmail } from '../lib/format';
import { DOCUMENTO_AUSENTE, JA_ASSINADO, SCREEN_COPY } from '../lib/screenCopy';
import { buildLinearTrailSteps } from '../lib/trail';
import { resolveCeremonyScreen, type SignPhase } from '../lib/ceremonyScreen';
import { initialQualifiedStep, initialSimpleStep, type QualifiedStep, type SimpleStep } from '../lib/ceremonySteps';
import { filterCertsByExpectation } from '../lib/cpf';
import { DocumentCard } from '../components/DocumentCard';
import { DocumentCardSkeleton } from '../components/Skeleton';
import { StateCard } from '../components/StateCard';
import { BridgePanel } from '../components/BridgePanel';
import { CertList } from '../components/CertList';
import { SuccessPanel } from '../components/SuccessPanel';
import { ConsentCard } from '../components/ConsentCard';
import { ParticipantsTrail } from '../components/ParticipantsTrail';
import { PdfViewer, type ViewerLoadState } from '../components/PdfViewer';
// Subpath browser-safe (DI2-1) — nunca o index de @argink/shared para import de VALOR.
import { CONSENT_TERMS_VERSION } from '@argink/shared/constants';

export interface SignPageProps {
  signerId: string;
  envelopeId: string;
}

type LoadPhase = 'loading' | 'loaded' | 'error';
type SimpleBusy = 'requesting_otp' | 'verifying_otp' | 'consenting' | 'completing' | null;

const AGENT_STEPS = ['Abrindo sessão', 'Aguardando sua autorização', 'Assinando neste computador', 'Concluindo'];

/**
 * Configuração de download do ARGINK Bridge — lida uma vez, fora do componente
 * (não muda em runtime). Fonte única em `lib/bridge-download.ts`; `null` quando esta
 * implantação não publicou instalador, e nesse caso `BridgePanel` orienta a pedir o
 * instalador a quem enviou o documento, em vez de oferecer um botão morto.
 */
const BRIDGE_DOWNLOAD_CONFIG = getBridgeDownloadConfig();
const SIMPLE_STEPS = ['Identificação', 'Verificação do canal', 'Consentimento', 'Assinatura', 'Sucesso'];
const QUALIFIED_STEPS = ['Identificação', 'Consentimento', 'Certificado', 'Assinatura', 'Sucesso'];

/** Expectativa de CPF (#92): 'loading' bloqueia a lista até saber se há filtro;
 * 'error' é fail-closed (nenhum certificado exibido) com retry explícito. */
type ExpectationState =
  | { kind: 'loading' }
  | { kind: 'error'; error: HumanError }
  | { kind: 'ready'; cpfMatch: string | null; maskedCpf: string | null };

export function SignPage({ signerId, envelopeId }: SignPageProps) {
  const [loadPhase, setLoadPhase] = useState<LoadPhase>('loading');
  const [loadError, setLoadError] = useState<HumanError | null>(null);
  const [envelope, setEnvelope] = useState<EnvelopeView | null>(null);
  const [stageBlocked, setStageBlocked] = useState(false);

  const [selectedCert, setSelectedCert] = useState<AgentCert | null>(null);
  const [expectation, setExpectation] = useState<ExpectationState>({ kind: 'loading' });
  const [visibleCerts, setVisibleCerts] = useState<AgentCert[] | null>(null);

  const [signPhase, setSignPhase] = useState<SignPhase>('idle');
  const [signError, setSignError] = useState<HumanError | null>(null);
  const [revisionNotice, setRevisionNotice] = useState(false);
  const [completeResult, setCompleteResult] = useState<{ downloadPath: string; envelopeCompleted: boolean } | null>(null);

  const [simpleStep, setSimpleStep] = useState<SimpleStep>('identification');
  const [qualStep, setQualStep] = useState<QualifiedStep>('identification');
  const [simpleBusy, setSimpleBusy] = useState<SimpleBusy>(null);
  const [simpleError, setSimpleError] = useState<HumanError | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [presentedAt, setPresentedAt] = useState<string | null>(null);
  const [simpleSessionOpen, setSimpleSessionOpen] = useState(false);

  const [viewerState, setViewerState] = useState<ViewerLoadState>('loading');

  /**
   * Posicionamento do carimbo (M5a). `visualDraft` é a posição que a TELA mostra enquanto o
   * signatário arrasta; `activeSigner.visual` é a que o SERVIDOR confirmou. Enquanto não houver
   * rascunho, a verdade é a do servidor — e uma gravação recusada devolve a tela a ela, em vez
   * de deixar o carimbo num lugar que o documento não vai ter.
   */
  const [visualDraft, setVisualDraft] = useState<VisualPlacement | null>(null);
  const [visualSaving, setVisualSaving] = useState(false);
  const [visualError, setVisualError] = useState<HumanError | null>(null);
  /** A tela perdeu a sincronia com o servidor depois de um conflito — nenhuma gravação até voltar. */
  const [visualOutOfSync, setVisualOutOfSync] = useState(false);
  /**
   * Arrastar produz gravações em sequência rápida. Sem um número de ordem, a resposta de um PUT
   * antigo que chega depois de um novo sobrescreveria a posição mais recente — o carimbo
   * "voltaria" sozinho. Só a resposta da gravação mais nova pode escrever no estado.
   */
  const visualCommitSeq = useRef(0);
  /**
   * Versão do posicionamento conhecida pelo cliente, enviada como precondição (`If-Match`). É o
   * que impede que um retry de rede de uma gravação ANTIGA sobrescreva a escolha mais nova e
   * responda 200 — o contador de ordem abaixo só protege esta tela, não o servidor.
   * `null` até a primeira gravação: aí o servidor aceita sem precondição.
   */
  const visualVersionRef = useRef<number | null>(null);



  /**
   * Número de ordem de TODA carga de envelope (a normal e a silenciosa do pós-conflito). Sem ele,
   * uma resposta antiga que chega depois de uma nova sobrescreve a mais recente e a tela volta a
   * mostrar um estado que já não existe — inclusive a `visual_version`, que vira a precondição do
   * próximo gesto e garante um 409 novo.
   */
  const envelopeLoadSeq = useRef(0);

  const loadEnvelope = useCallback(async () => {
    const seq = envelopeLoadSeq.current + 1;
    envelopeLoadSeq.current = seq;
    setLoadPhase('loading');
    setLoadError(null);
    try {
      const env = await getEnvelope(envelopeId);
      if (seq !== envelopeLoadSeq.current) return;
      setEnvelope(env);
      setStageBlocked(false);
      setLoadPhase('loaded');
      /**
       * A carga bem-sucedida é a SAÍDA do estado travado do carimbo (M5a). Sem isto o botão
       * "Recarregar a assinatura" recarregava de verdade e a marca continuava desabilitada, com o
       * mesmo cartão de erro na tela: uma saída que não sai. A tela voltou a saber onde a marca
       * está, então a interação volta junto.
       */
      setVisualOutOfSync(false);
      setVisualError(null);
      setVisualDraft(null);
      visualVersionRef.current = null;
    } catch (err) {
      if (seq !== envelopeLoadSeq.current) return;
      setLoadError(describeError(err));
      setLoadPhase('error');
    }
  }, [envelopeId]);

  useEffect(() => { void loadEnvelope(); }, [loadEnvelope]);

  const signer = envelope?.signers.find((candidate) => candidate.id === signerId) ?? null;
  const isSimples = signer?.required_signature_level === 'simples';
  const canSign =
    envelope?.status === 'in_progress' &&
    signer?.role === 'signer' &&
    (signer.status === 'notified' || signer.status === 'in_session');

  /**
   * Onboarding do ARGINK Bridge (R-UX02-P03) — só ativo na etapa de certificado da
   * Qualificada, exatamente a condição que antes disparava `checkProvider()` "à mão"
   * (ver M6/#93: consentir vem ANTES de escolher a chave). O hook cuida de tudo daqui
   * pra frente: checagem inicial, polling pós-download, listagem de certificados —
   * nenhum `useState` de loading espalhado neste componente.
   */
  const bridgeOnboardingActive = Boolean(loadPhase === 'loaded' && canSign && !isSimples && qualStep === 'certificate');
  const { state: bridgeState, retry: retryBridge, startDownload: startBridgeDownload } = useBridgeOnboarding(bridgeOnboardingActive);

  /**
   * Janela para posicionar o carimbo (M5a). Fecha quando a assinatura em si começa, e a razão é
   * do PIPELINE, não da tela: em avancada/qualificada a aparência entra nos
   * `SignatureImageParameters` ANTES do `getDataToSign` — com a sessão aberta, o DTBS já está
   * congelado e mover o carimbo faria a tela prometer um lugar e o PDF trazer outro. Na Simples
   * o selo só lê a posição no `complete`, mas a tela fecha no mesmo ponto de propósito: o mesmo
   * gesto não pode valer numa modalidade e ser recusado na outra. O servidor recusa de todo
   * jeito (`placement_frozen`) — isto aqui é a tela não oferecer o que seria negado.
   */
  const canPlaceStamp =
    canSign && (isSimples ? simpleStep !== 'signature' : qualStep !== 'certificate');

  /**
   * Versão que o servidor informou no carregamento. Faz o PRIMEIRO gesto já sair com precondição —
   * antes disso, a primeira gravação ia sem `If-Match` e ficava exposta a replay.
   */
  const serverVisualVersion = signer?.visual_version ?? null;

  const commitVisual = useCallback(
    async (next: VisualPlacement) => {
      const seq = visualCommitSeq.current + 1;
      visualCommitSeq.current = seq;
      setVisualSaving(true);
      setVisualError(null);
      try {
        const saved = await setVisualPlacement(
          signerId,
          next,
          visualVersionRef.current ?? serverVisualVersion,
        );
        if (seq !== visualCommitSeq.current) return;
        visualVersionRef.current = saved.version;
        setVisualDraft(saved.visual);
      } catch (err) {
        if (seq !== visualCommitSeq.current) return;
        setVisualError(describeError(err));
        // Volta para o que o servidor tem: a janela pode ter fechado (409) no meio do gesto.
        setVisualDraft(null);
        /**
         * Depois de um conflito, a tela volta a PERGUNTAR ao servidor — e esquece a versão local.
         *
         * As duas coisas juntas, porque cada uma sozinha erra de um jeito: só esquecer a versão
         * fazia o gesto seguinte sair sem `If-Match` e sobrescrever cegamente a escrita de outra
         * aba; só recarregar deixava a ref presa na versão velha, e todo gesto seguinte tomava 409
         * para sempre. Zerando a ref, a próxima gravação usa `serverVisualVersion`, que é a versão
         * que esta recarga trouxe.
         *
         * A recarga é SILENCIOSA (não passa por `loadEnvelope`): aquela devolve a tela ao estado
         * "carregando" e apaga a mensagem que acabou de explicar o conflito ao signatário. Trocar
         * a cerimônia inteira por causa de um carimbo é resposta grande demais para o problema.
         */
        visualVersionRef.current = null;
        const cargaSeq = envelopeLoadSeq.current + 1;
        envelopeLoadSeq.current = cargaSeq;
        void getEnvelope(envelopeId)
          .then((atual) => {
            if (cargaSeq !== envelopeLoadSeq.current) return;
            setEnvelope(atual);
            setVisualOutOfSync(false);
          })
          .catch(() => {
            if (cargaSeq !== envelopeLoadSeq.current) return;
            /**
             * A recarga falhou: a tela NÃO sabe mais onde a marca está de verdade. Continuar
             * aceitando gestos aqui seria pior que parar — cada um sairia com uma precondição
             * velha, tomaria 409 e o signatário ficaria repetindo um gesto que nunca funciona,
             * sem entender por quê. A marca congela e a tela pede uma recarga explícita.
             */
            setVisualOutOfSync(true);
          });
      } finally {
        if (seq === visualCommitSeq.current) setVisualSaving(false);
      }
    },
    [signerId, serverVisualVersion, envelopeId],
  );

  // Retomada (#96): passo inicial derivado UMA vez, do fato apurado pelo servidor —
  // recargas posteriores do envelope (pós-assinatura etc.) não regridem o passo local.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (loadPhase !== 'loaded' || !signer || resumedRef.current) return;
    resumedRef.current = true;
    if (signer.required_signature_level === 'simples') {
      setSimpleStep(initialSimpleStep(signer));
    } else {
      setQualStep(initialQualifiedStep(signer));
    }
  }, [loadPhase, signer]);

  // Expectativa de CPF (#92) — buscada junto com o passo de certificado.
  const loadExpectation = useCallback(async () => {
    setExpectation({ kind: 'loading' });
    try {
      const view = await getCertificateExpectation(signerId);
      setExpectation({ kind: 'ready', cpfMatch: view.cpf_match, maskedCpf: view.masked_cpf });
    } catch (err) {
      setExpectation({ kind: 'error', error: describeError(err) });
    }
  }, [signerId]);

  useEffect(() => {
    if (loadPhase === 'loaded' && canSign && !isSimples && qualStep === 'certificate') void loadExpectation();
  }, [loadPhase, canSign, isSimples, qualStep, loadExpectation]);

  // Filtro #92: aplica a expectativa sobre a lista do provider (`bridgeState.certs`,
  // publicada por `useBridgeOnboarding` só em CERTIFICATES_READY/NO_COMPATIBLE_CERTIFICATE).
  // Incompatível NUNCA entra em `visibleCerts` (nem desabilitado); sem expectativa
  // (null) = sem filtro.
  useEffect(() => {
    let cancelled = false;
    if (bridgeState.certs === null || expectation.kind !== 'ready') {
      setVisibleCerts(null);
      return;
    }
    void filterCertsByExpectation(bridgeState.certs, envelopeId, expectation.cpfMatch).then((filtered) => {
      if (!cancelled) setVisibleCerts(filtered);
    });
    return () => {
      cancelled = true;
    };
  }, [bridgeState.certs, expectation, envelopeId]);

  // `presented_at` do consentimento: o instante em que o TERMO foi apresentado —
  // marcado quando o passo de consentimento aparece (qualquer modalidade).
  const consentVisible =
    (isSimples && simpleStep === 'consent') || (!isSimples && qualStep === 'consent');
  useEffect(() => {
    if (consentVisible && presentedAt === null) setPresentedAt(new Date().toISOString());
  }, [consentVisible, presentedAt]);

  function catchStage(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 409 && err.body?.error === 'stage_not_available') {
      setStageBlocked(true);
      return true;
    }
    return false;
  }

  async function handleQualifiedSign() {
    if (!selectedCert || !bridgeState.provider) return;
    const provider = bridgeState.provider.kind;
    setSignError(null);
    setRevisionNotice(false);
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const authorizeTraceId = newTraceId();
        try {
          setSignPhase('opening_session');
          // A cadeia é do PROVIDER, não do item da lista: pelo Bridge o DER é buscado
          // agora, só para o certificado escolhido, e conferido contra o id pedido.
          const certChainB64 = await providerCertChain(provider, selectedCert, authorizeTraceId);
          const session = await openSession(signerId, { cert_chain: certChainB64 }, authorizeTraceId);
          setSignPhase('authorizing_agent');
          const completeTraceId = newTraceId();
          setSignPhase('signing_agent');
          const signed = await signWithProvider(provider, {
            cert: selectedCert,
            certChainB64,
            sessionId: session.id,
            dtbsB64: session.dtbs_hash,
            // Contexto que o signatário lê no diálogo NATIVO, na língua dele — é o
            // último ponto em que ele pode recusar, então precisa dizer o que assina.
            purpose: `Assinar "${envelope?.documents[0]?.name ?? 'documento'}" na ARGINK`,
            authorizeTraceId,
            signTraceId: completeTraceId,
          });
          const completeBody: CompleteSessionBody = {
            signed_value: signed.signatureValueB64,
            algorithm: signed.algorithm || 'RSA_SHA256',
          };
          setSignPhase('completing');
          const result = await completeSession(signerId, completeBody, completeTraceId);
          setCompleteResult({ downloadPath: result.signed_document.download_path, envelopeCompleted: result.envelope_completed });
          setSignPhase('success');
          void loadEnvelope();
          return;
        } catch (err) {
          if (catchStage(err)) return;
          const stale = err instanceof ApiError && err.status === 409 && err.body?.error === 'document_revision_stale';
          if (stale && attempt === 0) {
            setRevisionNotice(true);
            setSignPhase('revision_stale');
            continue;
          }
          throw err;
        }
      }
    } catch (err) {
      setSignError(describeError(err));
      setSignPhase('error');
    }
  }

  async function sendOtp() {
    setSimpleBusy('requesting_otp');
    setSimpleError(null);
    try {
      await requestOtp(signerId);
      setOtpSent(true);
    } catch (err) {
      if (!catchStage(err)) setSimpleError(specificSimpleError(err, 'request'));
    } finally {
      setSimpleBusy(null);
    }
  }

  async function confirmOtp() {
    setSimpleBusy('verifying_otp');
    setSimpleError(null);
    try {
      await verifyOtp(signerId, otpCode);
      setSimpleStep('consent');
    } catch (err) {
      if (!catchStage(err)) setSimpleError(specificSimpleError(err, 'verify'));
    } finally {
      setSimpleBusy(null);
    }
  }

  async function acceptSimpleConsent() {
    if (!consentChecked || !presentedAt) return;
    setSimpleBusy('consenting');
    setSimpleError(null);
    try {
      if (!simpleSessionOpen) {
        await openSession(signerId, {});
        setSimpleSessionOpen(true);
      }
      await giveConsent(signerId, { consentVersion: CONSENT_TERMS_VERSION, presentedAt });
      setSimpleStep('signature');
    } catch (err) {
      if (!catchStage(err)) setSimpleError(specificSimpleError(err, 'consent'));
    } finally {
      setSimpleBusy(null);
    }
  }

  // Qualificada (#93): consentimento SEM sessão prévia (a sessão nasce depois,
  // com a cert_chain) e SEM OTP — a autenticação desta modalidade é a posse da
  // chave, provada na assinatura (D-035). Mesmo termo/rota/evento da Simples.
  async function acceptQualifiedConsent() {
    if (!consentChecked || !presentedAt) return;
    setSimpleBusy('consenting');
    setSimpleError(null);
    try {
      await giveConsent(signerId, { consentVersion: CONSENT_TERMS_VERSION, presentedAt });
      setQualStep('certificate');
    } catch (err) {
      if (!catchStage(err)) setSimpleError(specificSimpleError(err, 'consent'));
    } finally {
      setSimpleBusy(null);
    }
  }

  async function completeSimple() {
    setSimpleBusy('completing');
    setSimpleError(null);
    try {
      const result = await completeSession(signerId, {});
      setCompleteResult({ downloadPath: result.signed_document.download_path, envelopeCompleted: result.envelope_completed });
      setSignPhase('success');
      void loadEnvelope();
    } catch (err) {
      if (!catchStage(err)) setSimpleError(specificSimpleError(err, 'complete'));
    } finally {
      setSimpleBusy(null);
    }
  }

  const handleChooseAnotherCert = useCallback(() => {
    setSelectedCert(null);
    setSignError(null);
    setSignPhase('idle');
    retryBridge();
  }, [retryBridge]);

  const screen = resolveCeremonyScreen({ loadPhase, envelope, signer, signPhase, signError, stageBlocked, isSimples });

  if (screen.kind === 'loading') return <main className="page"><Header /><DocumentCardSkeleton /></main>;
  if (screen.kind === 'load_error') {
    return (
      <main className="page">
        <Header />
        <StateCard
          tone="error"
          title={loadError?.title ?? 'Não foi possível carregar o documento'}
          detail={loadError?.detail}
          traceId={loadError?.traceId}
          action={<Button variant="outline" onClick={() => void loadEnvelope()}>Tentar de novo</Button>}
        />
      </main>
    );
  }
  if (screen.kind === 'empty') {
    return <main className="page"><Header /><StateCard tone="empty" {...SCREEN_COPY.empty} /></main>;
  }
  if (screen.kind === 'signer_not_found') {
    return (
      <main className="page">
        <Header />
        <StateCard tone="error" {...SCREEN_COPY.signer_not_found} />
      </main>
    );
  }

  // A partir daqui, `resolveCeremonyScreen` só devolve os kinds abaixo quando
  // `envelope`/`signer` existem (contrato coberto por ceremonyScreen.test.ts) —
  // narrow único e documentado em vez de `!` espalhado pelo resto do arquivo.
  const activeEnvelope = envelope as NonNullable<typeof envelope>;
  const activeSigner = signer as NonNullable<typeof signer>;

  const shell = (content: JSX.Element) => (
    <main className="page">
      <Header />
      <DocumentCard envelope={activeEnvelope} signer={activeSigner} />
      {content}
    </main>
  );

  // Layout documento-cêntrico (#91): sidebar (fluxo + trilha) à esquerda,
  // documento na área principal. Só os fluxos ativos usam este layout — os
  // estados terminais continuam na coluna única de sempre (shell).
  const ceremonyShell = (flow: JSX.Element) => {
    const doc = activeEnvelope.documents[0];
    return (
      <main className="page page--ceremony">
        <Header />
        <div className="ceremony-layout">
          <aside className="ceremony-sidebar" aria-label="Fluxo da assinatura">
            <DocumentCard envelope={activeEnvelope} signer={activeSigner} />
            {flow}
            <ParticipantsTrail envelope={activeEnvelope} currentSignerId={activeSigner.id} />
          </aside>
          <section className="ceremony-document" aria-label="Documento para assinar">
            {doc ? (
              <>
                <PdfViewer
                  envelopeId={activeEnvelope.id}
                  documentId={doc.id}
                  visual={visualDraft ?? activeSigner.visual}
                  onLoadState={setViewerState}
                  {...(canPlaceStamp
                    ? { onVisualChange: setVisualDraft, onVisualCommit: commitVisual }
                    : {})}
                  visualLocked={visualSaving || visualOutOfSync}
                />
                {canPlaceStamp && (visualDraft ?? activeSigner.visual) ? (
                  <p className="ceremony-document__hint card__note">
                    Arraste a marca da sua assinatura até onde ela deve aparecer no documento — ou
                    mova com as setas do teclado. A posição fica gravada para a assinatura.
                  </p>
                ) : null}
                {visualError ? (
                  <StateCard
                    tone="error"
                    title="Não foi possível guardar a posição da assinatura"
                    detail={
                      visualOutOfSync
                        ? `${visualError.detail} A tela também não conseguiu conferir onde a marca está agora, então ela ficou travada — recarregue antes de mover de novo.`
                        : `${visualError.detail} A marca voltou para a última posição confirmada.`
                    }
                    traceId={visualError.traceId}
                    {...(visualOutOfSync
                      ? {
                          action: (
                            <Button variant="outline" onClick={() => void loadEnvelope()}>
                              Recarregar a assinatura
                            </Button>
                          ),
                        }
                      : {})}
                  />
                ) : null}
              </>
            ) : (
              <StateCard tone="error" {...DOCUMENTO_AUSENTE} />
            )}
          </section>
        </div>
      </main>
    );
  };

  switch (screen.kind) {
    case 'observer':
      return shell(<StateCard tone="info" {...SCREEN_COPY.observer} />);
    case 'not_started':
      return shell(<StateCard tone="info" {...SCREEN_COPY.not_started} />);
    case 'envelope_expired':
      return shell(<StateCard tone="error" {...SCREEN_COPY.envelope_expired} />);
    case 'envelope_cancelled':
      return shell(<StateCard tone="error" {...SCREEN_COPY.envelope_cancelled} />);
    case 'envelope_declined':
      return shell(<StateCard tone="info" {...SCREEN_COPY.envelope_declined} />);
    case 'signer_declined':
      return shell(<StateCard tone="info" title="Você recusou este documento" detail={activeSigner.decline_reason ?? undefined} />);
    case 'signer_expired':
      return shell(<StateCard tone="error" {...SCREEN_COPY.signer_expired} />);
    case 'success': {
      const doc = activeEnvelope.documents[0];
      const downloadPath = completeResult?.downloadPath ?? (doc ? signedDownloadPath(activeEnvelope.id, doc.id) : null);
      return shell(
        downloadPath ? (
          <SuccessPanel
            title="Documento assinado com sucesso"
            downloadPath={downloadPath}
            fileHint={`${doc?.name ?? 'documento'}-assinado.pdf`}
            envelopeCompleted={completeResult?.envelopeCompleted ?? activeEnvelope.status === 'completed'}
            envelopeId={activeEnvelope.id}
          />
        ) : (
          <StateCard tone="info" {...JA_ASSINADO} />
        ),
      );
    }
    case 'stage_blocked':
      return shell(
        <StateCard
          tone="info"
          {...SCREEN_COPY.stage_blocked}
          action={<Button variant="outline" onClick={() => void loadEnvelope()}>Verificar novamente</Button>}
        />,
      );
    case 'terminal_error':
      return shell(<StateCard tone="error" title={signError!.title} detail={signError!.detail} traceId={signError!.traceId} />);
    case 'simple': {
      const simpleIndex = simpleStep === 'identification' ? 0 : simpleStep === 'channel' ? 1 : simpleStep === 'consent' ? 2 : 3;
      return ceremonyShell(
        <>
          <TrailStepper aria-label="Progresso da assinatura" steps={buildLinearTrailSteps(SIMPLE_STEPS, simpleIndex)} />
          {simpleStep === 'identification' ? (
            <Card aria-labelledby="identification-title">
              {/* M6-C: a frase começava repetindo "Assinatura Simples — Verificação por
                  e-mail e registro da manifestação", palavra por palavra, três centímetros
                  abaixo do `DocumentCard`, que já a diz. O que só existe AQUI, e é o que
                  esta declaração serve para afirmar, é a natureza do selo — que ele é da
                  plataforma e não do signatário. Repetir o nome da modalidade não reforçava
                  nada: gastava a atenção justamente antes da frase que importa. */}
              <p className="simple-declaration">
                <strong>O documento será selado com certificado da plataforma ARGINK</strong> — não é
                um certificado seu.
              </p>
              <h2 id="identification-title" className="card__title">1. Confira sua identificação</h2>
              <p><strong>Quem assina:</strong> {activeSigner.name}</p>
              <p><strong>Canal:</strong> {maskEmail(activeSigner.email)}</p>
              <Button variant="primary" onClick={() => setSimpleStep('channel')}>Continuar para verificar o e-mail</Button>
            </Card>
          ) : null}

          {simpleStep === 'channel' ? (
            <Card aria-labelledby="channel-title">
              <h2 id="channel-title" className="card__title">2. Verifique seu canal</h2>
              <p className="card__note">Enviaremos um código de 6 dígitos para {maskEmail(activeSigner.email)}.</p>
              <Button variant="outline" disabled={simpleBusy !== null} onClick={() => void sendOtp()}>
                {otpSent ? 'Reenviar código' : 'Enviar código'}
              </Button>
              {otpSent ? (
                <div className="otp-row">
                  <TextField
                    id="otp-code"
                    label="Código de 6 dígitos"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <Button variant="primary" disabled={otpCode.length !== 6 || simpleBusy !== null} onClick={() => void confirmOtp()}>
                    Verificar código
                  </Button>
                </div>
              ) : null}
            </Card>
          ) : null}

          {simpleStep === 'consent' ? (
            <>
              {activeSigner.channel_verified_at ? (
                <p className="resume-note card__note" role="status">
                  E-mail verificado em {formatDate(activeSigner.channel_verified_at)} — você não precisa repetir o código.
                </p>
              ) : null}
              <ConsentCard
                envelope={activeEnvelope}
                signer={activeSigner}
                viewerReady={viewerState === 'loaded'}
                checked={consentChecked}
                onCheckedChange={setConsentChecked}
                onAccept={() => void acceptSimpleConsent()}
                busy={simpleBusy === 'consenting'}
                error={null}
                stepNumber={3}
              />
            </>
          ) : null}

          {simpleStep === 'signature' ? (
            <Card aria-labelledby="simple-sign-title">
              <h2 id="simple-sign-title" className="card__title">4. Concluir assinatura</h2>
              {activeSigner.consent_given_at ? (
                <p className="resume-note" role="status">
                  Você já consentiu em {formatDate(activeSigner.consent_given_at)} — pode concluir a assinatura.
                </p>
              ) : (
                <p>Seu e-mail foi verificado e o consentimento foi registrado. Confirme para aplicar o selo da plataforma.</p>
              )}
              <Button variant="primary" disabled={simpleBusy !== null} isLoading={simpleBusy === 'completing'} onClick={() => void completeSimple()}>
                Concluir assinatura simples
              </Button>
            </Card>
          ) : null}

          {simpleError ? <StateCard tone="error" title={simpleError.title} detail={simpleError.detail} traceId={simpleError.traceId} /> : null}
        </>,
      );
    }
    case 'qualified': {
      const qualIndex =
        qualStep === 'identification' ? 0 : qualStep === 'consent' ? 1 : signPhase === 'idle' || signPhase === 'error' ? 2 : 3;
      const stepIndex =
        signPhase === 'opening_session' || signPhase === 'revision_stale'
          ? 0
          : signPhase === 'authorizing_agent'
            ? 1
            : signPhase === 'signing_agent'
              ? 2
              : signPhase === 'completing'
                ? 3
                : -1;
      const providerReady = bridgeState.provider !== null;
      const signDisabled = !selectedCert || !providerReady || (signPhase !== 'idle' && signPhase !== 'error');
      // Só a fase RECOVERABLE_ERROR por falha de LISTAGEM chega aqui com `provider`
      // ainda preenchido (o programa está pronto; só a listagem falhou) — o timeout
      // de INSTALAÇÃO nunca deixa `provider` set, então nunca cai neste ramo. Por
      // isso o único código de erro que `CertList` (que já tem a UI de "Tentar de
      // novo" certa para isto) precisa tratar é `certificates_failed`.
      const certsError: HumanError | null =
        bridgeState.phase === 'RECOVERABLE_ERROR' && bridgeState.error?.reason === 'certificates_failed'
          ? { title: 'Não foi possível listar seus certificados', detail: bridgeState.error.detail, traceId: bridgeState.error.traceId ?? '' }
          : null;
      const certsLoading =
        bridgeState.phase === 'DISCOVERING_CERTIFICATES' ||
        expectation.kind === 'loading' ||
        (bridgeState.certs !== null && visibleCerts === null);
      return ceremonyShell(
        <>
          <TrailStepper aria-label="Progresso da assinatura" steps={buildLinearTrailSteps(QUALIFIED_STEPS, qualIndex)} />
          {qualStep === 'identification' ? (
            <Card aria-labelledby="q-identification-title">
              {/* Mesma correção do ramo Simples (M6-C): o nome e a descrição da modalidade
                  já estão no `DocumentCard` logo acima. O que pertence a esta declaração é a
                  contrapartida — onde a chave fica. É a afirmação que sustenta o invariante
                  D-001 diante de quem assina, e ela não pode dividir o parágrafo com uma
                  repetição. */}
              <p className="simple-declaration">
                <strong>A assinatura acontece com o SEU certificado</strong>, pelo assistente desta
                máquina — a chave nunca sai do seu computador.
              </p>
              <h2 id="q-identification-title" className="card__title">1. Confira sua identificação</h2>
              <p><strong>Quem assina:</strong> {activeSigner.name}</p>
              <p><strong>Canal:</strong> {maskEmail(activeSigner.email)}</p>
              <Button variant="primary" onClick={() => setQualStep('consent')}>Continuar para o consentimento</Button>
            </Card>
          ) : null}

          {qualStep === 'consent' ? (
            <ConsentCard
              envelope={activeEnvelope}
              signer={activeSigner}
              viewerReady={viewerState === 'loaded'}
              checked={consentChecked}
              onCheckedChange={setConsentChecked}
              onAccept={() => void acceptQualifiedConsent()}
              busy={simpleBusy === 'consenting'}
              error={simpleError}
              stepNumber={2}
            />
          ) : null}

          {qualStep === 'certificate' ? (
            <>
              {activeSigner.consent_given_at ? (
                <p className="resume-note card__note" role="status">
                  Você já consentiu em {formatDate(activeSigner.consent_given_at)} — escolha o certificado para assinar.
                </p>
              ) : null}
              <section aria-labelledby="agent-title" className="section">
                <h2 id="agent-title" className="section__title">3. Escolha seu certificado</h2>
                <BridgePanel
                  state={bridgeState}
                  onRetry={retryBridge}
                  onDownload={startBridgeDownload}
                  downloadConfig={BRIDGE_DOWNLOAD_CONFIG}
                />
                {providerReady && expectation.kind === 'error' ? (
                  <StateCard
                    tone="error"
                    title="Não foi possível preparar o filtro de certificados"
                    detail={`${expectation.error.detail} Por privacidade, nenhum certificado é listado sem essa confirmação.`}
                    traceId={expectation.error.traceId}
                    action={<Button variant="outline" onClick={() => void loadExpectation()}>Tentar de novo</Button>}
                  />
                ) : null}
                {providerReady && expectation.kind !== 'error' ? (
                  <CertList
                    certs={visibleCerts}
                    loading={certsLoading}
                    error={certsError}
                    selectedKeyId={selectedCert?.keyId ?? null}
                    onSelect={setSelectedCert}
                    onRetry={retryBridge}
                    expectedMaskedCpf={expectation.kind === 'ready' ? expectation.maskedCpf : null}
                  />
                ) : null}
              </section>
              {revisionNotice ? (
                <StateCard
                  tone="info"
                  title="O documento ganhou nova assinatura durante a sua — reabrindo sessão"
                  detail="Confirme novamente para autorizar esta versão do documento."
                />
              ) : null}
              {signPhase !== 'idle' ? (
                <TrailStepper aria-label="Progresso da assinatura" steps={buildLinearTrailSteps(AGENT_STEPS, Math.max(stepIndex, 0))} />
              ) : null}
              {signPhase === 'error' && signError ? (
                <StateCard
                  tone="error"
                  title={signError.title}
                  detail={signError.detail}
                  traceId={signError.traceId}
                  action={signError.certificateKind ? <Button variant="outline" onClick={handleChooseAnotherCert}>Escolher outro certificado</Button> : undefined}
                />
              ) : null}
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => void handleQualifiedSign()}
                disabled={signDisabled}
                isLoading={signPhase !== 'idle' && signPhase !== 'error'}
              >
                {signPhase === 'error' ? 'Tentar assinar de novo' : 'Assinar com o certificado selecionado'}
              </Button>
            </>
          ) : null}
        </>,
      );
    }
    default:
      return null;
  }
}

function Header() {
  return (
    <header className="page__header">
      <BrandMark variant="wordmark" className="page__brand" />
      <h1 className="page__title">Assinatura de documento</h1>
    </header>
  );
}
